'use client';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { CalendarIcon, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

interface DashboardHeaderProps {
  dateRange: { startDate: Date; endDate: Date };
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
}

// 좁은 화면(<640px)에서는 날짜 2개와 새로고침 버튼이 한 줄에 들어가도록 짧은 포맷을 쓴다
function DateLabel({ date, fallback }: { date?: Date; fallback: string }) {
  if (!date) return <>{fallback}</>;
  const d = dayjs(date).locale('ko');
  return (
    <>
      <span className='sm:hidden'>{d.format('YY.MM.DD')}</span>
      <span className='hidden sm:inline'>{d.format('YYYY년 MM월 DD일')}</span>
    </>
  );
}

export function DashboardHeader({
  dateRange,
  onStartDateChange,
  onEndDateChange,
}: DashboardHeaderProps) {
  const [currentDate, setCurrentDate] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    updateCurrentDate();
  }, []);

  const updateCurrentDate = () => {
    setCurrentDate(new Date().toLocaleString('ko-KR'));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    updateCurrentDate();

    // 새로고침 이벤트 발생
    window.dispatchEvent(new CustomEvent('dashboard-refresh'));

    // 10초 제한
    setTimeout(() => {
      setIsRefreshing(false);
    }, 10000);
  };

  const handleStartDateSelect = (date: Date | undefined) => {
    onStartDateChange(date);
    setStartOpen(false);
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    onEndDateChange(date);
    setEndOpen(false);
  };

  return (
    <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
      <h1 className='text-2xl text-gray-900 whitespace-nowrap'>
        대시보드
        <small className='block text-sm font-normal text-gray-600 sm:inline sm:ml-2'>
          ({currentDate} 기준)
        </small>
      </h1>

      <div className='flex flex-wrap items-center gap-2'>
        <div className='flex items-center gap-1'>
          <Popover open={startOpen} onOpenChange={setStartOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'justify-start text-left font-normal',
                  !dateRange.startDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='h-4 w-4' />
                <DateLabel date={dateRange.startDate} fallback='시작일' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0'>
              <Calendar
                mode='single'
                selected={dateRange.startDate}
                onSelect={handleStartDateSelect}
                disabled={date => date > new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <span className='text-sm text-muted-foreground'>~</span>

          <Popover open={endOpen} onOpenChange={setEndOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className={cn(
                  'justify-start text-left font-normal',
                  !dateRange.endDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className='h-4 w-4' />
                <DateLabel date={dateRange.endDate} fallback='종료일' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0'>
              <Calendar
                mode='single'
                selected={dateRange.endDate}
                onSelect={handleEndDateSelect}
                disabled={date =>
                  date > new Date() || date < dateRange.startDate
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <Button
          variant='outline'
          size='sm'
          className='h-8 w-8 p-0 rounded-full'
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </Button>
      </div>
    </div>
  );
}
